import { html as cmHtml } from "@codemirror/lang-html";
import { Badge, Button, Input } from "@exhousing/design-system";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import {
  AlignLeft,
  ArrowLeft,
  Code2,
  Eye,
  FileText,
  PanelRightClose,
  PanelRightOpen,
  RotateCcw,
  Save,
  Search,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  StyledTemplateEditor,
  type StyledTemplateEditorHandle,
} from "@/components/document/StyledTemplateEditor";
import { prettyHtml } from "@/lib/prettyHtml";
import {
  previewTemplate,
  useDeleteTemplate,
  useDocumentTemplates,
  useTemplateBaseCss,
  useTemplateFields,
  useUpsertTemplate,
} from "@/hooks/api/useDocuments";

type Template = {
  id: string;
  organization_id: string | null;
  type: string;
  variant: string;
  language: string;
  name: string;
  body: string;
};

function typeLabel(type: string): string {
  const words = type.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function variantLanguageLabel(t: Template): string {
  const variant = t.variant ? `${t.variant.toUpperCase()} · ` : "";
  return `${variant}${t.language.toUpperCase()}`;
}

const SIGN_ROLES = [
  { role: "tenant", label: "Tenant signature" },
  { role: "landlord", label: "Landlord signature" },
  { role: "guarantor", label: "Guarantor signature" },
] as const;

export function DocumentStudioPage() {
  const { data: templates } = useDocumentTemplates();
  const { data: fieldGroups } = useTemplateFields();
  const { data: baseCss } = useTemplateBaseCss();
  const upsert = useUpsertTemplate();
  const reset = useDeleteTemplate();

  const [selected, setSelected] = useState<Template | null>(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState("");
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [mode, setMode] = useState<"code" | "visual">("code");
  const [showPreview, setShowPreview] = useState(true);
  const [showFields, setShowFields] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const editorRef = useRef<StyledTemplateEditorHandle>(null);

  // Group templates by document type for the navigator tree.
  const groups = useMemo(() => {
    const list = (templates ?? []) as Template[];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? list.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.type.replace(/_/g, " ").includes(q),
        )
      : list;
    const byType = new Map<string, Template[]>();
    for (const t of filtered) {
      const arr = byType.get(t.type) ?? [];
      arr.push(t);
      byType.set(t.type, arr);
    }
    return [...byType.entries()].map(([type, items]) => ({ type, items }));
  }, [templates, query]);

  const runPreview = useCallback((value: string) => {
    previewTemplate(value).then((r) => {
      setPreview(r.html);
      setPreviewError(r.error);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runPreview(body), 350);
    return () => clearTimeout(debounce.current);
  }, [body, selected, runPreview]);

  // Legal-class templates (legacy contracts) are full HTML documents — they edit in code
  // mode only; the styled editor is for content-only letter bodies.
  const isLegalDoc = (b: string) => /^\s*<(!doctype|html)/i.test(b);

  function open(t: Template) {
    // Pretty-print on open so the source is readable; it's render-equivalent.
    const body = prettyHtml(t.body);
    setSelected(t);
    setName(t.name);
    setBody(body);
    if (isLegalDoc(t.body)) setMode("code");
    runPreview(body);
  }

  function insertRaw(snippet: string) {
    if (mode === "visual") {
      editorRef.current?.insertSnippet(snippet);
      return;
    }
    const view = cmRef.current?.view;
    if (view) {
      view.dispatch(view.state.replaceSelection(snippet));
      view.focus();
    } else {
      navigator.clipboard?.writeText(snippet);
      toast.success(`Copied ${snippet}`);
    }
  }

  const insertToken = (token: string) => insertRaw(`{{ ${token} }}`);

  function save() {
    if (!selected) return;
    upsert.mutate(
      {
        type: selected.type,
        variant: selected.variant,
        language: selected.language as "nl" | "en",
        name,
        body,
      },
      {
        onSuccess: () => toast.success("Saved as your agency's version"),
        onError: (e) => toast.error(e.message),
      },
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* Slim chrome — replaces the app sidebar for full-display focus. */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Console
          </Link>
          <span className="font-display text-base font-medium tracking-tight">
            Document studio
          </span>
          {selected && (
            <Badge tone={selected.organization_id ? "brand" : "neutral"}>
              {selected.organization_id ? "Custom" : "Default"}
            </Badge>
          )}
        </div>
        {selected && (
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border p-0.5">
              <button
                type="button"
                onClick={() => setMode("code")}
                className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium ${
                  mode === "code"
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                <Code2 className="size-3.5" />
                Code
              </button>
              <button
                type="button"
                disabled={isLegalDoc(selected.body)}
                title={
                  isLegalDoc(selected.body)
                    ? "Legal contracts edit in code mode"
                    : undefined
                }
                onClick={() => setMode("visual")}
                className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
                  mode === "visual"
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                <Eye className="size-3.5" />
                Visual
              </button>
            </div>
            {mode === "code" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBody((b) => prettyHtml(b))}
                title="Pretty-print the HTML"
              >
                <AlignLeft className="size-3.5" />
                Format
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview((s) => !s)}
            >
              {showPreview ? (
                <PanelRightClose className="size-4" />
              ) : (
                <PanelRightOpen className="size-4" />
              )}
              Preview
            </Button>
            {selected.organization_id && (
              <Button
                variant="outline"
                size="sm"
                disabled={reset.isPending}
                onClick={() =>
                  reset.mutate(selected.id, {
                    onSuccess: () => {
                      toast.success("Reset to the default template");
                      setSelected(null);
                    },
                    onError: (e) => toast.error(e.message),
                  })
                }
              >
                <RotateCcw className="size-4" />
                Reset
              </Button>
            )}
            <Button size="sm" onClick={save} disabled={upsert.isPending}>
              <Save className="size-4" />
              Save
            </Button>
          </div>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Navigator — grouped file tree of templates. */}
        <nav className="flex w-72 shrink-0 flex-col border-r border-border">
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {!templates ? (
              <p className="px-2 py-4 text-sm text-muted-foreground">Loading…</p>
            ) : (
              groups.map((g) => (
                <div key={g.type} className="mb-3">
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {typeLabel(g.type)}
                  </p>
                  {g.items.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => open(t)}
                      className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                        selected?.id === t.id
                          ? "bg-secondary font-medium"
                          : "hover:bg-muted"
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{t.name}</span>
                      </span>
                      <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                        {variantLanguageLabel(t)}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </nav>

        {/* Editor — the work. */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <FileText className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                Choose a template from the left to start editing.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-8 max-w-md text-sm font-medium"
                />
                <button
                  type="button"
                  onClick={() => setShowFields((s) => !s)}
                  className="shrink-0 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  {showFields ? "Hide fields" : "Insert field"}
                </button>
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setSigOpen((o) => !o)}
                    className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    Insert signature ▾
                  </button>
                  {sigOpen && (
                    <>
                      <button
                        type="button"
                        aria-hidden
                        tabIndex={-1}
                        onClick={() => setSigOpen(false)}
                        className="fixed inset-0 z-10 cursor-default"
                      />
                      <div className="absolute left-0 z-20 mt-1 w-44 rounded-md border border-border bg-background py-1 shadow-md">
                        {SIGN_ROLES.map((r) => (
                          <button
                            key={r.role}
                            type="button"
                            onClick={() => {
                              insertRaw(`{{ sign_field("${r.role}") }}`);
                              setSigOpen(false);
                            }}
                            className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {previewError && (
                  <span className="text-xs font-medium text-destructive">
                    Template error
                  </span>
                )}
              </div>

              {showFields && fieldGroups && (
                <div className="max-h-44 overflow-auto border-b border-border bg-muted/40 px-4 py-2.5">
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    {fieldGroups.map((fg) => (
                      <div key={fg.group} className="min-w-[8rem]">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {fg.group}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {fg.fields.map((field) => (
                            <button
                              key={field.token}
                              type="button"
                              onClick={() => insertToken(field.token)}
                              title={`Example: ${field.example}`}
                              className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] hover:border-primary hover:text-primary"
                            >
                              {field.token}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-hidden">
                {mode === "visual" ? (
                  baseCss ? (
                    <StyledTemplateEditor
                      key={selected.id}
                      ref={editorRef}
                      initialValue={body}
                      onChange={setBody}
                      baseCss={baseCss}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Loading editor…
                    </div>
                  )
                ) : (
                  <CodeMirror
                    ref={cmRef}
                    value={body}
                    height="100%"
                    className="h-full text-sm"
                    extensions={[cmHtml()]}
                    onChange={(v) => setBody(v)}
                    basicSetup={{ lineNumbers: true, foldGutter: false }}
                  />
                )}
              </div>
            </>
          )}
        </main>

        {/* Preview — the truth (live A4 render). */}
        {showPreview && selected && (
          <aside className="flex w-[42%] shrink-0 flex-col border-l border-border bg-muted/30">
            <div className="flex items-center justify-between border-b border-border px-4 py-2 text-xs font-medium text-muted-foreground">
              <span>Live preview · sample data</span>
              {previewError && <span className="text-destructive">⚠ error</span>}
            </div>
            {previewError ? (
              <pre className="flex-1 overflow-auto bg-destructive/5 p-3 text-xs text-destructive">
                {previewError}
              </pre>
            ) : (
              <iframe
                title="Template preview"
                srcDoc={preview}
                sandbox=""
                className="flex-1 bg-white"
              />
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
